/**
 * What the user has connected to their Symphony agents, and the ability to disconnect it — now
 * rendered only by the Connectors page's Channels tab, split between its AI-coders and Gemini
 * sections. The standalone `/settings/connections` screen was retired in Phase 5; its route still
 * resolves, onto that tab (`docs/connectors-page-design.md` §8.1).
 *
 * This is what makes the consent meaningful: a grant that cannot be withdrawn is not something the
 * user agreed to. Disconnecting revokes immediately and closes every thread the agent opened.
 */

import { useState } from 'react';
import { ExternalLink, MessageSquare } from 'lucide-react';
import { ChatGptGlyph, ClaudeGlyph, GeminiGlyph } from '../connectors/BrandGlyphs';
import { Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { formatConversationDate } from '../../../lib/conversations';
import { useAgentConnections, useDisconnectAgent, type AgentConnection } from '../../../lib/agent-connections';
import { conversationLink } from '../sidebar/shared';
import { ConnectClaudeCard } from './ConnectClaudeCard';
import { ConnectAppCard } from './ConnectAppCard';
import { matchesChannelTitle, type AiCoderTitleKey } from '../connectors/channel-search';
import styles from './ConnectionsPage.module.css';

const CONNECTIONS_ROUTE = 'settings-connections';

/**
 * The MCP server both Claude (web & desktop) and ChatGPT point their custom connector at. A literal
 * endpoint value the user pastes — never localized.
 */
export const MCP_SERVER_URL = 'https://symphony.wix.com/agent/mcp';

/**
 * The A2A Agent Card the user imports into Gemini Enterprise. Static config, built through
 * JSON.stringify so it is guaranteed valid and pretty-printed; a literal value, never localized.
 */
export const GEMINI_AGENT_CARD = JSON.stringify(
  {
    protocolVersion: '0.3',
    name: 'Symphony',
    description: 'Grow your business with a team of AI agents that spot opportunities.',
    url: 'https://symphony.wix.com/individuals-chat/poc/a2a',
    iconUrl: 'https://symphony.wix.com/300-site-logo.png',
    preferredTransport: 'JSONRPC',
    provider: { organization: 'Wix', url: 'https://www.wix.com' },
    version: '1.0.0',
    defaultInputModes: ['text/plain'],
    defaultOutputModes: ['text/plain'],
    capabilities: { streaming: true, pushNotifications: false },
    skills: [
      {
        id: 'symphony_converse',
        name: 'Work with your Symphony agents',
        description:
          'Hold a conversation with your Symphony agents and have them act on your business: scheduling, outreach, follow-ups and reporting.',
        tags: ['assistant', 'scheduling', 'outreach', 'business'],
        examples: ['What needs my attention today?'],
      },
    ],
    securitySchemes: {
      oauth2: {
        type: 'oauth2',
        flows: {
          authorizationCode: {
            authorizationUrl: 'https://symphony.wix.com/agent-oauth/authorize',
            tokenUrl: 'https://symphony.wix.com/individuals-chat/poc/v1/agent/oauth/token',
            scopes: { 'symphony.act_as_me': 'Act on your Symphony agents on your behalf' },
          },
        },
      },
    },
    security: [{ oauth2: ['symphony.act_as_me'] }],
  },
  null,
  2,
);

/** A connection is a Gemini Enterprise one when its platform names Gemini; everything else (Claude
 *  Code, ChatGPT, other MCP clients) belongs to the AI-coders group. Shared so the two Channels
 *  sections partition the list the same way and no connection lands in both or neither. */
export function isGeminiConnection(c: AgentConnection): boolean {
  return (c.agentPlatform ?? '').toLowerCase().includes('gemini');
}

/**
 * The AI-coder connect cards: Claude Code (mints a token inline), then Claude web & desktop and
 * ChatGPT (each a copyable MCP endpoint).
 *
 * ⚠️ `ConnectClaudeCard` MINTS AN ACT-AS-ME TOKEN. This is a security surface, not a layout element —
 * do not change how the token is requested, gated or displayed when moving this around
 * (docs/connectors-page-design.md §4).
 */
export function AiCodersConnect({ query = '' }: { query?: string } = {}) {
  const { t } = useTranslation();
  const productName = useProductName();
  // A coder-name query narrows to the matching card(s) — the section is already visible because one of
  // these titles matched. The same keys drive both decisions, as on native and on mobile.
  const show = (key: AiCoderTitleKey) => matchesChannelTitle(t(key), query);
  return (
    <>
      {show('connections.claude.title') && <ConnectClaudeCard />}
      {show('connections.connect.claudeWeb.title') && (
        <ConnectAppCard
          icon={<ClaudeGlyph size={18} />}
          title={t('connections.connect.claudeWeb.title')}
          instructions={t('connections.connect.claudeWeb.instructions', { productName })}
          value={MCP_SERVER_URL}
          copyBiName="copy-claude-web-url"
        />
      )}
      {show('connections.connect.chatgpt.title') && (
        <ConnectAppCard
          icon={<ChatGptGlyph size={18} />}
          title={t('connections.connect.chatgpt.title')}
          instructions={t('connections.connect.chatgpt.instructions')}
          value={MCP_SERVER_URL}
          copyBiName="copy-chatgpt-url"
        />
      )}
    </>
  );
}

/** The Gemini Enterprise connect card — the Agent Card JSON, collapsed behind View since it is long
 *  and Copy is what the user needs. Lives in the Gemini section, not with the AI coders. */
export function GeminiConnect() {
  const { t } = useTranslation();
  return (
    <ConnectAppCard
      icon={<GeminiGlyph size={16.5} />}
      title={t('connections.connect.gemini.title')}
      instructions={t('connections.connect.gemini.instructions')}
      value={GEMINI_AGENT_CARD}
      copyBiName="copy-gemini-card"
      collapsible
    />
  );
}

/**
 * The connected-agent list — what has authority over the account, and the ability to disconnect it.
 * `filter` narrows it to one section's platforms; `showEmpty` prints the "nothing connected" line
 * for the standalone route (a per-section list stays quiet when it has nothing, since the section's
 * own rows already carry its state).
 *
 * A failed fetch is NOT an empty list. Collapsing the two would say nothing is connected when we
 * simply could not find out — the one reading of this screen that must never be wrong. iOS and
 * Android draw the same distinction.
 */
export function AgentConnectionsList({
  filter,
  showEmpty = false,
}: {
  filter?: (c: AgentConnection) => boolean;
  showEmpty?: boolean;
}) {
  const { t } = useTranslation();
  const productName = useProductName();
  const { connections, loading, error } = useAgentConnections();
  useLoadBi('agent-connections-data', { isLoading: loading, isError: error });
  const shown = filter ? connections.filter(filter) : connections;

  if (error) {
    return (
      <Card>
        <Typography variant="h4">{t('connections.error')}</Typography>
        <Typography variant="p2" color="secondary">
          {t('connections.error.sub')}
        </Typography>
      </Card>
    );
  }
  if (!loading && shown.length === 0) {
    return showEmpty ? (
      <Card>
        <Typography variant="p2" color="secondary">
          {t('connections.empty', { productName })}
        </Typography>
      </Card>
    ) : null;
  }
  return (
    <div className={styles.list}>
      {groupConnections(shown).map((group) =>
        group.connections.length === 1 ? (
          <ConnectionCard key={group.connections[0].id} connection={group.connections[0]} />
        ) : (
          <ConnectionGroupCard key={group.key} group={group} />
        ),
      )}
    </div>
  );
}

/** One app's grants, folded together. `key` is the label the reader sees, which is also what makes them
 *  look like duplicates in the first place. */
export interface ConnectionGroup {
  key: string;
  connections: AgentConnection[];
}

/**
 * Fold an app's grants into one row.
 *
 * Every connect mints a NEW grant rather than replacing the last one, and a client that does not revoke
 * its predecessor (Claude Code re-runs, an app reconnecting) leaves one row per connect behind. A reader
 * with a handful of apps ends up scrolling a page of near-identical cards that all say the same name.
 *
 * Grouped by the label the reader sees, not by any id: the id is not on the wire, and the label is exactly
 * what makes two rows look like the same thing. A single grant is left completely alone — folding one row
 * into a group of one would add a disclosure to press for nothing.
 *
 * Order is preserved: the first time a label appears is where its group sits, so grouping never reshuffles
 * a list the reader may already have scanned.
 */
export function groupConnections(connections: AgentConnection[]): ConnectionGroup[] {
  const groups = new Map<string, ConnectionGroup>();
  for (const connection of connections) {
    const key = `${connection.agentName ?? ''}\u0000${connection.agentPlatform ?? ''}`;
    const group = groups.get(key);
    if (group) {
      group.connections.push(connection);
    } else {
      groups.set(key, { key, connections: [connection] });
    }
  }
  return [...groups.values()];
}

/** The newest date in a group, so a folded row reports the app's real last activity. */
export function latestDate(dates: (string | undefined)[]): string | undefined {
  const usable = dates.filter((date): date is string => !!date);
  return usable.length ? usable.reduce((latest, date) => (Date.parse(date) > Date.parse(latest) ? date : latest)) : undefined;
}

/** The oldest date in a group — when this app FIRST got access, which is the one worth showing. */
export function earliestDate(dates: (string | undefined)[]): string | undefined {
  const usable = dates.filter((date): date is string => !!date);
  return usable.length ? usable.reduce((first, date) => (Date.parse(date) < Date.parse(first) ? date : first)) : undefined;
}

function ConnectionGroupCard({ group }: { group: ConnectionGroup }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [open, setOpen] = useState(false);
  const first = group.connections[0];
  const label = first.agentName || first.agentPlatform || t('connections.unknownAgent');
  const openThreads = group.connections.reduce((total, connection) => total + (connection.openThreadCount ?? 0), 0);
  // The group's own timeline: when this app first got access, and the last time ANY of its grants was
  // used. Summing the parts, rather than showing the first row's dates and hoping they represent the rest.
  const timeline = [
    earliestDate(group.connections.map((connection) => connection.connectedDate)),
    latestDate(group.connections.map((connection) => connection.lastUsedDate)),
  ];

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        <div className={styles.details}>
          <Typography variant="h4">{label}</Typography>
          {first.agentPlatform ? (
            <Typography variant="p3" color="secondary">
              {first.agentPlatform}
            </Typography>
          ) : null}
          <Typography variant="p3" color="muted">
            {t('connections.group.count', { count: group.connections.length })} ·{' '}
            {openThreads ? t('connections.openThreads', { count: openThreads }) : t('connections.noOpenThreads')}
          </Typography>
          <Typography variant="p3" color="muted">
            {[
              timeline[0] ? t('connections.connected', { date: formatConversationDate(timeline[0]) }) : null,
              timeline[1] ? t('connections.lastUsed', { date: formatConversationDate(timeline[1]) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </div>
        <div className={styles.actions}>
          <Button
            variant="text"
            size="small"
            aria-expanded={open}
            onClick={() => {
              setOpen((current) => !current);
              logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: open ? 'collapse-agent-grants' : 'expand-agent-grants' });
            }}
          >
            {open ? t('connections.group.hide') : t('connections.group.showAll', { count: group.connections.length })}
          </Button>
        </div>
      </div>
      {/* Every grant keeps its own row and its own Disconnect — the fold is about the page being readable,
          never about taking an action away. Revoking one connect must not revoke the others. */}
      {open ? (
        <div className={styles.groupMembers}>
          {group.connections.map((connection) => (
            <ConnectionCard key={connection.id} connection={connection} />
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function ConnectionCard({ connection }: { connection: AgentConnection }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const disconnect = useDisconnectAgent();
  const [confirming, setConfirming] = useState(false);

  const label = connection.agentName || connection.agentPlatform || t('connections.unknownAgent');

  // "Connected <date>" and, once it has been used, "Last used <date>" — joined with a middle dot into
  // one quiet line, so a user can tell an active connection from a stale one and prune accordingly.
  const timeline = [
    connection.connectedDate ? t('connections.connected', { date: formatConversationDate(connection.connectedDate) }) : null,
    connection.lastUsedDate ? t('connections.lastUsed', { date: formatConversationDate(connection.lastUsedDate) }) : null,
  ].filter(Boolean) as string[];

  const onDisconnect = () => {
    logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'disconnect-agent' });
    disconnect.mutate(connection.id);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.row}>
        <div className={styles.details}>
          <Typography variant="h4">{label}</Typography>
          {connection.agentPlatform ? (
            <Typography variant="p3" color="secondary">
              {connection.agentPlatform}
            </Typography>
          ) : null}
          <Typography variant="p3" color="muted">
            {connection.openThreadCount
              ? t('connections.openThreads', { count: connection.openThreadCount })
              : t('connections.noOpenThreads')}
          </Typography>
          {timeline.length ? (
            <Typography variant="p3" color="muted">
              {timeline.join(' · ')}
            </Typography>
          ) : null}
        </div>

        <div className={styles.actions}>
          {connection.conversationId ? (
            <Button
              variant="text"
              size="small"
              href={conversationLink(basePath, connection.conversationId).route}
              startIcon={<MessageSquare size={16} aria-hidden />}
              onClick={() => logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'view-conversation' })}
            >
              {t('connections.viewConversation')}
            </Button>
          ) : null}
          {connection.agentUrl ? (
            <Button
              variant="text"
              size="small"
              href={connection.agentUrl}
              external
              endIcon={<ExternalLink size={16} aria-hidden />}
              onClick={() => logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'open-agent' })}
            >
              {t('connections.open')}
            </Button>
          ) : null}

          {confirming ? (
            <>
              <Button color="destructive" size="small" loading={disconnect.isLoading} onClick={onDisconnect}>
                {t('connections.confirmDisconnect')}
              </Button>
              <Button variant="text" size="small" onClick={() => setConfirming(false)}>
                {t('common.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              color="destructive"
              size="small"
              onClick={() => {
                logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: 'disconnect-agent-intent' });
                setConfirming(true);
              }}
            >
              {t('connections.disconnect')}
            </Button>
          )}
        </div>
      </div>

      {/* Said at the point of decision rather than in a help article: this is what the button does. */}
      {confirming ? (
        <Typography variant="p3" color="secondary" className={styles.warning}>
          {t('connections.disconnectWarning', { agent: label })}
        </Typography>
      ) : null}
    </Card>
  );
}
